import { DataTable, DataTablePagination } from "@/src/components/ui/data-table";
import { useEffect, useState } from "react";
import { useFilters, usePageSize, usePageSort, useSearch } from "../state";
import { useNavigate } from "react-router-dom";
import { OperatorPayment, PayerType } from "../model";
import { operatorsColumns } from "./operatorsColumns";
import { paymentsFetch } from "@/src/lib/paymentsFetch";

type OperatorPaymentData = {
  data: OperatorPayment[];
} & DataTablePagination;

export function OperatorsTable({ shouldRefetch }: { shouldRefetch: boolean }) {
  const [loading, setLoading] = useState(false);
  const [operatorPayments, setOperatorPayments] = useState<OperatorPaymentData>({
    data: [],
    total: 1,
    pagination: { page: 1, pageSize: 1 },
  });
  const [page, setPage] = useState(1);

  const navigate = useNavigate();

  const search = useSearch();
  const pageSize = usePageSize();
  const { operatorSortBy } = usePageSort();
  const { customerId, operatorId, amountFrom, amountTo, amountCurrency, bookingRef, operatorRef } = useFilters();

  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: pageSize.toString(),
    orderBy: operatorSortBy,
    payerType: PayerType.OPERATOR,
  });

  // Append optional filters if available
  if (customerId) queryParams.append("customerId", customerId.toString());
  if (operatorId) queryParams.append("operatorId", operatorId.toString());
  if (amountFrom) queryParams.append("amountFrom", amountFrom.toString());
  if (amountTo) queryParams.append("amountTo", amountTo.toString());
  if (amountCurrency) queryParams.append("amountCurrency", amountCurrency.toString());
  queryParams.append("bookingRef", bookingRef ? bookingRef.toString() : "null");
  queryParams.append("operatorRef", operatorRef ? operatorRef.toString() : "null");

  // Final query string
  const queryString = queryParams.toString();

  const fetchOperatorsPayments = async () => {
    try {
      return await paymentsFetch<{ data: OperatorPayment[] } & DataTablePagination>(
        `/api/payment-schedule-step?${queryString}`,
        "GET",
        undefined,
        undefined,
      );
    } catch (error) {
      console.error("Error fetching customer payments", error);
    }
  };

  useEffect(() => {
    if (page > 1) {
      setPage(1);
    } else {
      fetchOperatorsPayments().then((data) => {
        if (data) {
          setOperatorPayments(data);
        } else {
          setOperatorPayments({
            data: [],
            total: 0,
            pagination: { page: 1, pageSize: 10 },
          });
        }
      });
    }
  }, [
    search,
    pageSize,
    operatorSortBy,
    customerId,
    operatorId,
    amountFrom,
    amountTo,
    amountCurrency,
    bookingRef,
    operatorRef,
    shouldRefetch,
  ]);

  useEffect(() => {
    setLoading(true);
    fetchOperatorsPayments().then((data) => {
      setLoading(false);
      if (data) {
        setOperatorPayments(data);
      } else {
        setOperatorPayments({
          data: [],
          total: 0,
          pagination: { page: 1, pageSize: 10 },
        });
      }
    });
  }, [page]);
  return (
    <div className="rounded-md bg-white border">
      <DataTable
        isLoading={loading}
        columns={operatorsColumns}
        data={operatorPayments?.data}
        pagination={operatorPayments}
        onPageClick={(page) => setPage(page)}
        onRowClick={(r) => navigate(`/bookings/${r.booking.id}?tab=payments`)}
      />
    </div>
  );
}
